import { useState, useCallback, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { AdminArticleListItem } from "@/types/article";
import type { PaginationMeta } from "@/types/common";
import type { UsePaginationReturn } from "@/hooks/use-pagination";
import {
  getArticleColumns,
  type ArticleAction,
} from "./article-columns";
import { ArticleCardList } from "./article-card-list";
import { ArticlePagination } from "./article-pagination";
import { PublishDialog } from "./publish-dialog";
import { DeleteDialog } from "./delete-dialog";
import { useNavigate } from "react-router-dom";

interface ArticleTableProps {
  data: AdminArticleListItem[];
  meta: PaginationMeta;
  pagination: UsePaginationReturn;
}

export function ArticleTable({
  data,
  meta,
  pagination,
}: ArticleTableProps) {
  const navigate = useNavigate();
  const [selectedArticle, setSelectedArticle] =
    useState<AdminArticleListItem | null>(null);
  const [dialogType, setDialogType] = useState<ArticleAction | null>(null);

  const handleAction = useCallback(
    (article: AdminArticleListItem, action: ArticleAction) => {
      if (action === "edit") {
        navigate(`/articles/${article._id}/edit`);
        return;
      }
      setSelectedArticle(article);
      setDialogType(action);
    },
    [navigate]
  );

  const handleDialogClose = useCallback(() => {
    setDialogType(null);
    setSelectedArticle(null);
  }, []);

  const columns = useMemo(
    () => getArticleColumns(handleAction),
    [handleAction]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <p className="text-muted-foreground">
          검색 조건에 맞는 아티클이 없습니다.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Desktop: Table view */}
      <div className="hidden md:block rounded-md border overflow-auto max-h-[calc(100vh-280px)]">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="hover:bg-muted/50 transition-colors duration-75"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile: Card view */}
      <div className="md:hidden">
        <ArticleCardList data={data} onAction={handleAction} />
      </div>

      <ArticlePagination meta={meta} pagination={pagination} />

      {selectedArticle && (
        <>
          <PublishDialog
            article={selectedArticle}
            action={dialogType === "publish" ? "publish" : "unpublish"}
            open={dialogType === "publish" || dialogType === "unpublish"}
            onOpenChange={(open) => {
              if (!open) handleDialogClose();
            }}
          />
          <DeleteDialog
            article={selectedArticle}
            open={dialogType === "delete"}
            onOpenChange={(open) => {
              if (!open) handleDialogClose();
            }}
          />
        </>
      )}
    </div>
  );
}
